import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Divider,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { FormikProvider, FieldArray } from 'formik';
import FormLabel from '../../common/form/FormLabel';
import EmpModalContainer from '../../../containers/common/form/modal/EmpModalContainer';
import NumberFormatText from '../../common/form/NumberFormatText';
import RoInternalCostsForm from './RoInternalCostsForm';

const SalesProfitForm = ({ formik, chance, orderInfo }) => {
  //chance.totalProfit:매출이익
  //chance.totalSales:매출액

  // roAdjProfitAmt(최종영업이익) = roProfitAmt(매출이익) - roInternalAmt(내부거래비용) - roCostAmt(예상영업비용)

  //전체갑(totalSales)에서 매출이익은 몇 %일까?
  // 최종 매출이익 계산기 (최신 값을 인자로 받도록 개선)
  const getTotalMargin = (updatedValues = {}) => {
    const totalSales = Number(chance?.totalSales) || 0;
    const roProfitAmt = Number(formik?.values?.roProfitAmt) || 0;
    
    // 💡 변경된 값이 인자로 들어오면 그걸 쓰고, 없으면 현재 formik 값을 씁니다.
    const roCostAmt = updatedValues.hasOwnProperty('roCostAmt') 
      ? Number(updatedValues.roCostAmt) 
      : Number(formik?.values?.roCostAmt) || 0;

    const roInternalCosts = updatedValues.hasOwnProperty('roInternalCosts')
      ? updatedValues.roInternalCosts
      : formik?.values?.roInternalCosts || [];

    const totalInternalAmt = _.sumBy(roInternalCosts, 'roInternalAmt') || 0;

    // 최종 조정 마진 계산
    const roAdjProfitAmt = roProfitAmt - roCostAmt - totalInternalAmt;
    
    // 💡 큰 금액 오차 방지를 위해 소수점 둘째 자리까지 반올림 (필요시 Math.round(percent)로 변경 가능)
    const percent = totalSales > 0 ? Math.round((roAdjProfitAmt / totalSales) * 10000) / 100 : 0;

    formik.setFieldValue('roAdjProfitAmt', roAdjProfitAmt);
    formik.setFieldValue('roAdjProfitAmtPercent', percent);
  };

  const handleChangeRoCost = () => {
    const totalSales = Number(chance?.totalSales) || 0;
    const numberValue = Number(formik?.values?.roCostAmt) || 0;
    
    const percent = totalSales > 0 ? Math.round((numberValue / totalSales) * 10000) / 100 : 0;

    formik.setFieldValue('roCostAmtPercent', percent);
    
    // 💡 방금 바뀐 최신 금액을 직접 꽂아줘서 비동기 타이밍 문제를 해결합니다.
    getTotalMargin({ roCostAmt: numberValue });
  };

  const handleChangeRoCostPercent = () => {
    const totalSales = Number(chance?.totalSales) || 0;
    const numberPercent = Number(formik?.values?.roCostAmtPercent) || 0;
    
    const numberValue = Math.round(totalSales * (numberPercent / 100));

    formik.setFieldValue('roCostAmt', numberValue);
    
    // 💡 방금 바뀐 최신 금액을 직접 꽂아줘서 비동기 타이밍 문제를 해결합니다.
    getTotalMargin({ roCostAmt: numberValue });
  };

  // 내부원가 리스트가 바뀔 때 마진 재계산
  useEffect(() => {
    if (formik?.values?.roInternalCosts) {
      getTotalMargin({ roInternalCosts: formik.values.roInternalCosts });
    }
  }, [formik?.values?.roInternalCosts]);


  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ width: '180px' }} textAlign="center">
            <FormLabel sx={{ px: 1 }}>매출이익</FormLabel>
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="roProfitAmt"
              name="roProfitAmt"
              value={formik.values.roProfitAmt}
              error={
                formik.touched.roProfitAmt && Boolean(formik.errors.roProfitAmt)
              }
              helperText={
                formik.touched.roProfitAmt && formik.errors.roProfitAmt
              }
              InputProps={{
                inputComponent: NumberFormatText,
              }}
              disabled
            />
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="roProfitAmtPercent"
              name="roProfitAmtPercent"
              value={formik.values.roProfitAmtPercent}
              error={
                formik.touched.roProfitAmtPercent &&
                Boolean(formik.errors.roProfitAmtPercent)
              }
              helperText={
                formik.touched.roProfitAmtPercent &&
                formik.errors.roProfitAmtPercent
              }
              InputProps={{
                inputComponent: NumberFormatText,
              }}
              disabled
              sx={{ width: '80PX' }}
            />
          </Box>
          <Box>%</Box>
        </Stack>
        <FormikProvider value={formik}>
          <FieldArray
            name="roInternalCosts"
            render={(arrayHelpers) => (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ width: 180 }} textAlign="center">
                    <FormLabel sx={{ px: 1 }}>내부거래비용</FormLabel>
                  </Box>
                  <Button
                    type="button"
                    color="secondary"
                    variant="outlined"
                    onClick={() =>
                      arrayHelpers.push({
                        roPartnerUsrNm: '',
                        roPartnerUsr: '',
                        roInternalAmt: 0,
                        roInternalAmtPercent: 0,
                      })
                    }
                  >
                    추가
                  </Button>
                </Box>
                <Box sx={{ py: 1 }}>
                  {formik.values?.roInternalCosts &&
                    formik.values?.roInternalCosts.map((roCost, index) => {
                      return (
                        <RoInternalCostsForm
                          key={index}
                          cost={roCost}
                          index={index}
                          touched={formik.touched}
                          errors={formik.errors}
                          handleChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          handleRemove={() => arrayHelpers.remove(index)}
                          totalSales={chance?.totalSales}
                          handleRoAdj={getTotalMargin}
                        />
                      );
                    })}
                </Box>
              </Box>
            )}
          ></FieldArray>
        </FormikProvider>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ width: '180px' }} textAlign="center">
            <FormLabel sx={{ px: 1 }}>영업비용</FormLabel>
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="roCostAmt"
              name="roCostAmt"
              value={formik.values.roCostAmt}
              onChange={(e) => {
                formik.setFieldValue('roCostAmt', e.target.value);
              }}
              onBlur={handleChangeRoCost}
              error={
                formik.touched.roCostAmt && Boolean(formik.errors.roCostAmt)
              }
              helperText={formik.touched.roCostAmt && formik.errors.roCostAmt}
              InputProps={{
                inputComponent: NumberFormatText,
              }}
            />
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="roCostAmtPercent"
              name="roCostAmtPercent"
              value={formik.values.roCostAmtPercent}
              onChange={(e) => {
                formik.setFieldValue('roCostAmtPercent', e.target.value);
              }}
              onBlur={handleChangeRoCostPercent}
              error={
                formik.touched.roCostAmtPercent &&
                Boolean(formik.errors.roCostAmtPercent)
              }
              helperText={
                formik.touched.roCostAmtPercent &&
                formik.errors.roCostAmtPercent
              }
              InputProps={{
                inputComponent: NumberFormatText,
              }}
              sx={{ width: '80PX' }}
            />
          </Box>
          <Box>%</Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            py: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ width: '180px' }} textAlign="center">
            <FormLabel sx={{ px: 1 }}>최종매출이익</FormLabel>
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="roAdjProfitAmt"
              name="roAdjProfitAmt"
              value={formik.values.roAdjProfitAmt}
              error={
                formik.touched.roAdjProfitAmt &&
                Boolean(formik.errors.roAdjProfitAmt)
              }
              helperText={
                formik.touched.roAdjProfitAmt && formik.errors.roAdjProfitAmt
              }
              InputProps={{
                inputComponent: NumberFormatText,
              }}
              disabled
            />
          </Box>
          <Box>
            <TextField
              size="small"
              required
              variant="outlined"
              fullWidth
              id="roAdjProfitAmtPercent"
              name="roAdjProfitAmtPercent"
              value={formik.values.roAdjProfitAmtPercent}
              error={
                formik.touched.roAdjProfitAmtPercent &&
                Boolean(formik.errors.roAdjProfitAmtPercent)
              }
              helperText={
                formik.touched.roAdjProfitAmtPercent &&
                formik.errors.roAdjProfitAmtPercent
              }
              InputProps={{
                inputComponent: NumberFormatText,
              }}
              disabled
              sx={{ width: '80PX' }}
            />
          </Box>
          <Box>%</Box>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SalesProfitForm;
